import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { can } from '@seller/shared';
import {
  api,
  ApiError,
  type ListingIssue,
  type TaxonomyCategory,
  type TaxonomyConfidence,
  type TaxonomyOverrideBody,
  type TaxonomyOverrideResult,
  type TaxonomyReviewResult,
  type TaxonomyReviewRow,
  type TaxonomySeriesCreateResult,
  type TaxonomySeriesRow,
  type TaxonomySource,
  type TaxonomyValues,
} from '../api/client.js';
import { Combobox } from '../components/Combobox.js';
import { useSession } from '../auth/useSession.js';
import {
  Alert,
  Button,
  Card,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
  Tabs,
} from '../components/ui.js';
import { integer } from '../format.js';
import { PUT_UP_LABEL } from '../lib/products.js';
import { savedAsNotes } from '../lib/saved-as.js';

/**
 * Product taxonomy review: work through the products the engine left unassigned
 * or weak, worst first — one card per product (child ASIN), showing its SKUs and
 * titles, the current assignment and where it came from, the dictionary series a
 * title matched (one-click), and an inline editor. A product whose Amazon product
 * type disagrees with its category (a listing issue) is queued at any confidence,
 * with its reason on the card; saving the category accepts that type, and
 * `?filter=listing-issues` shows only those. Products with no Active SKU are
 * retired and hidden unless `?include=retired`. The card's text fields are
 * pickers (components/Combobox.tsx) fed by `/api/taxonomy/values` for the chosen
 * category; the Series picker can add a new dictionary series from the card, and
 * the server lands a case-only variant on the spelling already in use. A second
 * tab edits the series dictionary. Follows the booking match-review pattern. See
 * docs/products.md.
 */

const CATEGORIES: TaxonomyCategory[] = ['racquet', 'string', 'grip', 'footwear', 'bag', 'other'];

const CATEGORY_LABELS: Record<TaxonomyCategory, string> = {
  racquet: 'Racquet',
  string: 'String',
  grip: 'Grip',
  footwear: 'Footwear',
  bag: 'Bag',
  other: 'Other',
};

const SOURCE_LABELS: Record<TaxonomySource, string> = {
  override: 'Owner',
  booking: 'Booking',
  heuristic: 'Title',
  catalog: 'Catalog',
};

const LISTING_ISSUE_LABELS: Record<ListingIssue, string> = {
  type_conflict: 'Product type conflict',
  type_unmapped: 'Unmapped product type',
};

const CONFIDENCE_BADGE: Record<TaxonomyConfidence, { label: string; className: string }> = {
  exact: { label: 'Exact', className: 'bg-ok-bg text-ok-text' },
  likely: { label: 'Likely', className: 'bg-brand-600/10 text-brand-600' },
  weak: { label: 'Weak', className: 'bg-warn-bg text-warn-text' },
  none: { label: 'Unassigned', className: 'bg-danger-bg text-danger-text' },
};

export function TaxonomyReviewPage() {
  const session = useSession();
  const canWrite = session.status === 'signedIn' && can(session.user.role, 'data:import');
  const [tab, setTab] = useState<'review' | 'dictionary'>('review');

  return (
    <div className="space-y-4">
      <div className="text-xs text-muted">
        <Link to="/products" className="hover:underline">
          Products
        </Link>{' '}
        / Taxonomy review
      </div>
      <PageHeader
        title="Product taxonomy review"
        description="Each card is one product (a child ASIN, or an ASIN-less SKU). Confirm or correct the category, series, model, color, and size the engine inferred — and, for a string, its gauge and put-up. A decision here is an owner override and is never overwritten by a later recompute."
      />

      <Tabs
        items={[
          { id: 'review', label: 'Review' },
          { id: 'dictionary', label: 'Series dictionary' },
        ]}
        active={tab}
        onChange={(id) => setTab(id as 'review' | 'dictionary')}
      />

      {tab === 'review' ? (
        <ReviewPanel canWrite={canWrite} />
      ) : (
        <DictionaryPanel canWrite={canWrite} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Review
// ---------------------------------------------------------------------------

function ReviewPanel({ canWrite }: { canWrite: boolean }) {
  const queryClient = useQueryClient();
  // `?filter=listing-issues` (the Products header's link) narrows the queue to the
  // products whose Amazon product type disagrees with their category.
  // `?include=retired` also lists the products with no Active SKU, hidden by
  // default. Both ride the URL (not local storage), so a shared link opens the
  // same queue.
  const [searchParams, setSearchParams] = useSearchParams();
  const onlyIssues = searchParams.get('filter') === 'listing-issues';
  const includeRetired = searchParams.get('include') === 'retired';
  const review = useQuery({
    queryKey: [
      'taxonomy-review',
      onlyIssues ? 'listing-issues' : 'all',
      includeRetired ? 'with-retired' : 'live',
    ],
    queryFn: () => {
      const params = new URLSearchParams();
      if (onlyIssues) params.set('filter', 'listing-issues');
      if (includeRetired) params.set('include', 'retired');
      const query = params.toString();
      return api.get<TaxonomyReviewResult>(`/api/taxonomy/review${query ? `?${query}` : ''}`);
    },
  });

  function setParam(name: string, value: string, on: boolean) {
    const params = new URLSearchParams(searchParams);
    if (on) params.set(name, value);
    else params.delete(name);
    setSearchParams(params, { replace: true });
  }
  const setOnlyIssues = (next: boolean) => setParam('filter', 'listing-issues', next);
  const setIncludeRetired = (next: boolean) => setParam('include', 'retired', next);
  const retired = review.data?.retired;

  // The latest save's "saved as the spelling already in use" notes, kept here
  // because a saved product usually leaves the queue (and its card) at once.
  const [savedAs, setSavedAs] = useState<{ product: string; notes: string[] } | null>(null);
  function onSavedAs(row: TaxonomyReviewRow, notes: string[]) {
    setSavedAs(
      notes.length === 0
        ? null
        : { product: row.asin ?? row.skus[0]?.sellerSku ?? `SKU ${row.skuId}`, notes },
    );
  }

  const recompute = useMutation({
    mutationFn: () => api.post<{ count: number }>('/api/taxonomy/recompute'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['taxonomy-review'] });
      void queryClient.invalidateQueries({ queryKey: ['taxonomy-summary'] });
    },
  });

  const rows = review.data?.rows ?? [];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {review.data ? (
          <span className="text-sm">
            <span className="font-medium text-body" data-testid="review-count">
              {integer(review.data.counts.remaining)} left to review
            </span>
            {review.data.counts.listingIssues > 0 && !onlyIssues && (
              <span className="text-muted" data-testid="review-issue-count">
                {' '}
                · {integer(review.data.counts.listingIssues)} listing issue
                {review.data.counts.listingIssues === 1 ? '' : 's'}
              </span>
            )}
            {retired && retired.count > 0 && (
              <span className="text-muted" data-testid="review-retired-count">
                {' '}
                ·{' '}
                {retired.included
                  ? `${integer(retired.count)} of them retired`
                  : `${integer(retired.count)} retired hidden`}
              </span>
            )}
          </span>
        ) : (
          <span className="text-sm font-medium text-muted">
            {review.isError ? 'Could not load the review queue' : 'Loading…'}
          </span>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-1.5 text-sm text-body">
            <input
              type="checkbox"
              checked={onlyIssues}
              onChange={(e) => setOnlyIssues(e.target.checked)}
              data-testid="only-listing-issues"
            />
            Listing issues only
          </label>
          <label
            className="flex items-center gap-1.5 text-sm text-body"
            title="Products none of whose SKUs is Active. Hidden by default: reviewing them adds nothing to the plan or the grids."
          >
            <input
              type="checkbox"
              checked={includeRetired}
              onChange={(e) => setIncludeRetired(e.target.checked)}
              data-testid="include-retired"
            />
            Include retired products
          </label>
          {canWrite && (
            <Button
              variant="secondary"
              onClick={() => recompute.mutate()}
              disabled={recompute.isPending}
            >
              {recompute.isPending ? 'Recomputing…' : 'Recompute all'}
            </Button>
          )}
        </div>
      </div>

      {(onlyIssues || (review.data?.counts.listingIssues ?? 0) > 0) && (
        <p className="text-xs text-muted" data-testid="listing-issues-help">
          A <span className="font-medium text-body">listing issue</span> means Amazon files the
          listing under a product type that disagrees with its category here, or one the app does
          not map. Fix the product type in Seller Central, or delete the listing if you no longer
          sell it, and the flag clears on the next catalog fetch. Or confirm the category here with
          &ldquo;Accept Amazon&apos;s type&rdquo; ticked to accept it as is; it comes back only if
          Amazon&apos;s type changes again. Editing other fields alone leaves the issue open.
        </p>
      )}

      {savedAs && (
        <Alert tone="info">
          <div
            className="flex flex-wrap items-start justify-between gap-2"
            data-testid="saved-notes"
          >
            <div>
              <span className="font-medium">Saved {savedAs.product}.</span>{' '}
              {savedAs.notes.join(' ')}
            </div>
            <button
              type="button"
              className="text-xs text-muted hover:underline"
              onClick={() => setSavedAs(null)}
            >
              Dismiss
            </button>
          </div>
        </Alert>
      )}

      {review.isPending && <Spinner label="Loading review" />}
      {review.isError && <Alert>{review.error.message}</Alert>}

      {review.data && rows.length === 0 && (
        <Card className="p-6">
          <p className="text-sm text-muted">
            {onlyIssues
              ? 'No listing issues — every Amazon product type agrees with its category, or has been confirmed.'
              : 'Nothing to review — every product is assigned with at least likely confidence.'}
            {retired && !retired.included && retired.count > 0 && (
              <>
                {' '}
                {integer(retired.count)} retired product{retired.count === 1 ? ' is' : 's are'}{' '}
                hidden; tick &ldquo;Include retired products&rdquo; to see{' '}
                {retired.count === 1 ? 'it' : 'them'}.
              </>
            )}
          </p>
        </Card>
      )}

      {rows.map((row) => (
        <ReviewCard
          key={row.asin ?? `sku:${row.skuId}`}
          row={row}
          canWrite={canWrite}
          onSavedAs={onSavedAs}
        />
      ))}
    </div>
  );
}

/** The key body identifying one product on a write. */
function keyBody(row: TaxonomyReviewRow): { asin: string } | { skuId: number } {
  return row.keyType === 'asin' ? { asin: row.asin! } : { skuId: row.skuId! };
}

function ReviewCard({
  row,
  canWrite,
  onSavedAs,
}: {
  row: TaxonomyReviewRow;
  canWrite: boolean;
  /** Called after a save with the fields stored in a different spelling than typed. */
  onSavedAs: (row: TaxonomyReviewRow, notes: string[]) => void;
}) {
  const queryClient = useQueryClient();
  const [category, setCategory] = useState<TaxonomyCategory | ''>(row.category ?? '');
  // The category the series/model/size/gauge/put-up on the card belong to; a
  // change to a different category clears them (they named the old one's values).
  const [fieldsCategory, setFieldsCategory] = useState<TaxonomyCategory | ''>(row.category ?? '');
  const [savedNotes, setSavedNotes] = useState<string[]>([]);
  const [series, setSeries] = useState(row.series ?? '');
  const [model, setModel] = useState(row.model ?? '');
  const [color, setColor] = useState(row.color ?? '');
  const [sizeLabel, setSizeLabel] = useState(row.sizeLabel ?? '');
  // A string's gauge (typed; the server canonicalizes "16g" or "1.30 mm") and put-up.
  const [gauge, setGauge] = useState(
    row.gauge ? (row.gaugeMm ? `${row.gauge} (${row.gaugeMm} mm)` : row.gauge) : '',
  );
  // The put-up as shown ("Mini reel"); the server canonicalizes what is typed.
  const [putUp, setPutUp] = useState(row.putUp ? PUT_UP_LABEL[row.putUp] : '');
  const [putUpLength, setPutUpLength] = useState(row.putUpLength ?? '');
  const [seriesNote, setSeriesNote] = useState<string | null>(null);

  // The pickers' lists for the chosen category; changing the category reloads them.
  const values = useQuery({
    queryKey: ['taxonomy-values', category],
    queryFn: () =>
      api.get<TaxonomyValues>(`/api/taxonomy/values?category=${encodeURIComponent(category)}`),
    enabled: category !== '',
    staleTime: 60_000,
  });
  const lists = category === '' ? undefined : values.data;

  // "Add 'X' as a new series": the dictionary row is created from the card (a
  // case-only match returns the existing series instead), then selected.
  const addSeries = useMutation({
    mutationFn: (text: string) =>
      api.post<TaxonomySeriesCreateResult>('/api/taxonomy/series', {
        category,
        series: text,
      }),
    onSuccess: (result, text) => {
      setSeries(result.series.series);
      setSeriesNote(
        result.created
          ? `Added “${result.series.series}” to the ${CATEGORY_LABELS[result.series.category]} series.`
          : `“${text}” is already in the dictionary as “${result.series.series}”.`,
      );
      void queryClient.invalidateQueries({ queryKey: ['taxonomy-values'] });
      void queryClient.invalidateQueries({ queryKey: ['taxonomy-series'] });
    },
    onError: (caught) =>
      setSeriesNote(caught instanceof ApiError ? caught.message : 'Could not add the series.'),
  });

  /**
   * The string fields to send with an override: only for a string (the server
   * drops them for anything else). The gauge goes as typed, "17 (1.25 mm)" too;
   * an untouched field keeps its engine source on the server.
   */
  function stringFields(forCategory: TaxonomyCategory | '') {
    if (forCategory !== 'string') return {};
    // Sent exactly as shown: the server reads "17 (1.25 mm)" as gauge 17 with a
    // 1.25 mm diameter. Keeping only the diameter would re-derive the gauge from
    // millimeters and could turn a correct 17 into 16L.
    return {
      gauge: gauge.trim() || null,
      putUp: putUp.trim() || null,
      putUpLength: putUpLength.trim() || null,
    };
  }

  /** The string fields of a save whose category change cleared them. */
  function stringFieldsCleared(forCategory: TaxonomyCategory | '') {
    return forCategory === 'string' ? { gauge: null, putUp: null, putUpLength: null } : {};
  }

  // On a card with a listing issue, whether this save accepts Amazon's type as
  // shown. Until the owner touches the box it follows the category: ticked while
  // the chosen category differs from the row's, unticked when the original is
  // picked back. Once ticked or unticked by hand, the hand choice wins. It is
  // always off (and disabled) while the category is unassigned: accepting a type
  // needs a category, and the server refuses it otherwise.
  const [manualAccept, setManualAccept] = useState<boolean | null>(null);

  function acceptFor(forCategory: TaxonomyCategory | '') {
    if (forCategory === '') return false;
    return manualAccept ?? forCategory !== (row.category ?? '');
  }
  const accept = acceptFor(category);

  /** Whether picking 
ext moves the card to a different category than its fields name. */
  function changesCategory(next: TaxonomyCategory | '') {
    return next !== '' && fieldsCategory !== '' && next !== fieldsCategory;
  }

  /**
   * Picks a category. Moving to a different one clears the series, model, size,
   * gauge, and put-up (with its length): they were the old category's values, and
   * saving them would canonicalize them onto the new category's lists. The color
   * stays (a color is the same in any category). Blanking the category keeps them,
   * so a pick back to the same category loses nothing.
   */
  function chooseCategory(next: TaxonomyCategory | '') {
    if (changesCategory(next)) {
      setSeries('');
      setModel('');
      setSizeLabel('');
      setGauge('');
      setPutUp('');
      setPutUpLength('');
      setSeriesNote(null);
    }
    if (next !== '') setFieldsCategory(next);
    setCategory(next);
  }

  /**
   * Whether this save acknowledges Amazon's product type. Every save sends the
   * category (a first override inherits nothing from the engine's guess, so an
   * omitted category would be saved blank), so the acknowledgment is explicit:
   * only a card that shows a listing issue — and so shows Amazon's type — can
   * accept it, with "Accept" ticked, sending the type the card showed. Anything
   * else sends `acknowledge: false`, which keeps the earlier acknowledgment; a
   * card with no issue never accepts a type it did not show (a category change
   * that now disagrees with Amazon brings the card back with the issue).
   */
  function acknowledgment(accepted = accept) {
    const accepts = row.listingIssue !== null && accepted;
    return accepts
      ? { acknowledgedCatalogType: row.catalogProductType }
      : { acknowledge: false as const };
  }

  const decide = useMutation({
    mutationFn: (body: TaxonomyOverrideBody) =>
      api.put<TaxonomyOverrideResult>('/api/taxonomy', body),
    onSuccess: (result, body) => {
      // Say which fields were stored in the spelling already in use, not as typed.
      const notes = result.saved
        ? savedAsNotes([
            {
              label: 'Series',
              typed: body.series,
              saved: result.saved.series,
              rule: result.saved.rules.series,
            },
            {
              label: 'Model',
              typed: body.model,
              saved: result.saved.model,
              rule: result.saved.rules.model,
            },
            {
              label: 'Color',
              typed: body.color,
              saved: result.saved.color,
              rule: result.saved.rules.color,
            },
            {
              label: 'Size',
              typed: body.sizeLabel,
              saved: result.saved.sizeLabel,
              rule: result.saved.rules.sizeLabel,
            },
          ])
        : [];
      setSavedNotes(notes);
      onSavedAs(row, notes);
      // A saved value can be new to the category's picker lists.
      void queryClient.invalidateQueries({ queryKey: ['taxonomy-values'] });
      void queryClient.invalidateQueries({ queryKey: ['taxonomy-review'] });
      void queryClient.invalidateQueries({ queryKey: ['taxonomy-summary'] });
      // The Products cards' "to review" badges count the same queue.
      void queryClient.invalidateQueries({ queryKey: ['products'] });
    },
    onError: (caught) => {
      // Amazon's type changed since the queue loaded: reload it so the card shows
      // the new type and reason; the refusal's message stays on the card.
      // "Accept" is cleared, so the new type is accepted only by ticking it again.
      if (caught instanceof ApiError && caught.code === 'CATALOG_TYPE_CHANGED') {
        setManualAccept(false);
        void queryClient.invalidateQueries({ queryKey: ['taxonomy-review'] });
      }
    },
  });

  function save() {
    decide.mutate({
      ...keyBody(row),
      category: category === '' ? null : category,
      series: series.trim() || null,
      model: model.trim() || null,
      color: color.trim() || null,
      sizeLabel: sizeLabel.trim() || null,
      ...stringFields(category),
      ...acknowledgment(),
    });
  }

  function applySuggestion(s: { category: TaxonomyCategory; series: string }) {
    // A suggestion in another category clears the old category's fields, the
    // same as picking that category by hand; read that before the state changes.
    const cleared = changesCategory(s.category);
    chooseCategory(s.category);
    setSeries(s.series);
    // Submit the full field set the owner sees, not just the suggestion: an
    // override records only the fields it is sent (an engine guess is not
    // inherited server-side), so the displayed model/color/size are carried too.
    decide.mutate({
      ...keyBody(row),
      category: s.category,
      series: s.series,
      model: cleared ? null : model.trim() || null,
      color: color.trim() || null,
      sizeLabel: cleared ? null : sizeLabel.trim() || null,
      ...(cleared ? stringFieldsCleared(s.category) : stringFields(s.category)),
      // The category state updates on the next render, so read the box as it will
      // be for the suggested category (the same rule picking it by hand follows).
      ...acknowledgment(acceptFor(s.category)),
    });
  }

  const badge = CONFIDENCE_BADGE[row.confidence];

  return (
    <Card className="p-3" testId="review-card">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="min-w-0">
          <span className="font-medium text-body">{row.titles[0] ?? '(no title)'}</span>
          {row.asin && <span className="ml-2 font-mono text-xs text-muted">{row.asin}</span>}
        </div>
        <span className="flex items-center gap-1.5 text-xs">
          {row.retired && (
            <span
              className="rounded bg-sunken px-1.5 py-0.5 font-medium text-muted"
              title="None of this product's SKUs is Active"
              data-testid="retired-badge"
            >
              Retired
            </span>
          )}
          {row.source && <span className="text-muted">{SOURCE_LABELS[row.source]}</span>}
          <span className={`rounded px-1.5 py-0.5 font-medium ${badge.className}`}>
            {badge.label}
          </span>
        </span>
      </div>

      {/* The SKUs on this product, so the owner can see the whole picture. */}
      <ul className="mt-1 space-y-0.5 text-xs text-muted">
        {row.skus.map((s) => (
          <li key={s.id}>
            <Link to={`/skus/${s.id}`} className="text-brand-600 hover:underline">
              {s.sellerSku}
            </Link>
            {s.status ? ` · ${s.status}` : ''}
            {s.title && s.title !== row.titles[0] ? ` · ${s.title}` : ''}
          </li>
        ))}
      </ul>

      {/* Amazon's product type disagrees with the category, or maps to none. */}
      {row.listingIssue && (
        <div
          className="mt-2 flex flex-wrap items-baseline gap-2 rounded-md bg-warn-bg px-2 py-1.5 text-xs text-warn-text ring-1 ring-inset ring-warn-line"
          data-testid="listing-issue"
        >
          <span className="font-medium">{LISTING_ISSUE_LABELS[row.listingIssue]}</span>
          <span>{row.listingIssueReason}</span>
          {canWrite && (
            <span className="text-muted">
              — fix it in Seller Central, or tick &ldquo;Accept Amazon&apos;s type&rdquo; below and
              save.
            </span>
          )}
        </div>
      )}

      {canWrite ? (
        <div className="mt-3 space-y-2">
          {row.suggestions.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-muted">Suggestions:</span>
              {row.suggestions.map((s) => (
                <Button
                  key={`${s.category}:${s.series}`}
                  variant="secondary"
                  disabled={decide.isPending}
                  onClick={() => applySuggestion(s)}
                >
                  {s.series}{' '}
                  <span className="text-xs text-muted">({CATEGORY_LABELS[s.category]})</span>
                </Button>
              ))}
            </div>
          )}

          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
            <Field label="Category">
              <Select
                value={category}
                onChange={(e) => chooseCategory(e.target.value as TaxonomyCategory | '')}
              >
                <option value="">— unassigned —</option>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {CATEGORY_LABELS[c]}
                  </option>
                ))}
              </Select>
            </Field>
            {/* Pickers: the values already in use for the category, plus free text.
                The server lands a case-only variant on the existing spelling. */}
            <Combobox
              label="Series"
              value={series}
              onChange={(next) => {
                setSeries(next);
                setSeriesNote(null);
              }}
              options={lists?.series ?? []}
              {...(category === '' ? { placeholder: 'Pick a category first' } : {})}
              {...(category !== '' && !addSeries.isPending
                ? {
                    onCreate: (text: string) => addSeries.mutate(text),
                    createLabel: (text: string) => `Add “${text}” as a new series`,
                  }
                : {})}
              testId="series-picker"
            />
            <Combobox
              label="Model"
              value={model}
              onChange={setModel}
              options={lists?.models ?? []}
            />
            <Combobox
              label="Color"
              value={color}
              onChange={setColor}
              options={lists?.colors ?? []}
            />
            <Combobox
              label="Size"
              value={sizeLabel}
              onChange={setSizeLabel}
              options={lists?.sizes ?? []}
            />
          </div>
          {savedNotes.length > 0 && (
            <ul className="text-xs text-muted" data-testid="card-saved-notes">
              {savedNotes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          )}
          {seriesNote && (
            <p className="text-xs text-muted" data-testid="series-note">
              {seriesNote}
            </p>
          )}

          {/* A string's gauge and put-up, each with where the engine found it. */}
          {category === 'string' && (
            <div className="grid gap-2 sm:grid-cols-3" data-testid="string-fields">
              <Combobox
                label="Gauge"
                value={gauge}
                onChange={setGauge}
                options={lists?.gauges ?? []}
                placeholder="16, 16L, or 1.30 mm"
              />
              <Combobox
                label="Put-up"
                value={putUp}
                onChange={setPutUp}
                options={(lists?.putUps ?? []).map((p) => PUT_UP_LABEL[p])}
                placeholder="Set, reel, mini reel, half set"
              />
              <Field label="Length">
                <Input
                  value={putUpLength}
                  onChange={(e) => setPutUpLength(e.target.value)}
                  placeholder="40 ft, 200 m"
                />
              </Field>
              <FieldSource label="Gauge" source={row.gaugeSource} />
              <FieldSource label="Put-up" source={row.putUpSource} />
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {row.listingIssue && (
              <label
                className={`flex items-center gap-1.5 text-xs ${category === '' ? 'text-muted' : 'text-body'}`}
                title={category === '' ? "Pick a category to accept Amazon's type" : undefined}
              >
                <input
                  type="checkbox"
                  checked={accept}
                  disabled={category === ''}
                  onChange={(e) => setManualAccept(e.target.checked)}
                  data-testid="accept-catalog-type"
                />
                Accept Amazon&apos;s type{' '}
                <span className="font-mono">{row.catalogProductType ?? '(none)'}</span> with this
                category
              </label>
            )}
            <Button onClick={save} disabled={decide.isPending}>
              {decide.isPending ? 'Saving…' : 'Save override'}
            </Button>
            {decide.isError && (
              <span className="text-xs text-danger-text">
                {decide.error instanceof ApiError ? decide.error.message : 'Could not save.'}
              </span>
            )}
          </div>
        </div>
      ) : (
        <p className="mt-2 text-xs text-muted">Sign in as an importer to assign products.</p>
      )}
    </Card>
  );
}

/** Where a string field came from ("Gauge from Title"), or that no source stated it. */
function FieldSource({ label, source }: { label: string; source: TaxonomySource | null }) {
  return (
    <span className="text-xs text-muted" data-testid={`source-${label.toLowerCase()}`}>
      {label}:{' '}
      {source ? (
        <span className="rounded bg-sunken px-1.5 py-0.5 font-medium text-body">
          {SOURCE_LABELS[source]}
        </span>
      ) : (
        'not stated anywhere'
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Series dictionary
// ---------------------------------------------------------------------------

function DictionaryPanel({ canWrite }: { canWrite: boolean }) {
  const queryClient = useQueryClient();
  const list = useQuery({
    queryKey: ['taxonomy-series'],
    queryFn: () => api.get<{ series: TaxonomySeriesRow[] }>('/api/taxonomy/series'),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['taxonomy-series'] });
  };

  const rows = list.data?.series ?? [];

  return (
    <div className="space-y-3">
      {canWrite && <AddSeriesForm onAdded={invalidate} />}

      {list.isPending && <Spinner label="Loading dictionary" />}
      {list.isError && <Alert>{list.error.message}</Alert>}

      {list.data && rows.length === 0 && (
        <Card className="p-6">
          <p className="text-sm text-muted">
            No series yet. Uploading and matching a booking seeds this from its series names; you
            can also add one above.
          </p>
        </Card>
      )}

      {rows.length > 0 && (
        <Card>
          <table className="w-full text-sm">
            <thead className="border-b border-line text-left text-xs text-muted">
              <tr>
                <th className="px-3 py-2 font-medium">Category</th>
                <th className="px-3 py-2 font-medium">Series</th>
                <th className="px-3 py-2 font-medium">Aliases</th>
                {canWrite && <th className="px-3 py-2 font-medium">Edit aliases</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {rows.map((s) => (
                <SeriesRowItem key={s.id} row={s} canWrite={canWrite} onSaved={invalidate} />
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

function AddSeriesForm({ onAdded }: { onAdded: () => void }) {
  const [category, setCategory] = useState<TaxonomyCategory>('racquet');
  const [series, setSeries] = useState('');
  const [aliases, setAliases] = useState('');
  const [error, setError] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: () =>
      api.post<TaxonomySeriesCreateResult>('/api/taxonomy/series', {
        category,
        series: series.trim(),
        aliases: aliases
          .split(',')
          .map((a) => a.trim())
          .filter((a) => a !== ''),
      }),
    onSuccess: (result) => {
      if (!result.created) {
        // A name that matches an existing series ignoring case is that series.
        setError(
          `“${series.trim()}” is already in the dictionary as “${result.series.series}”; edit its aliases below.`,
        );
        return;
      }
      setSeries('');
      setAliases('');
      setError(null);
      onAdded();
    },
    onError: (e: unknown) =>
      setError(e instanceof ApiError ? e.message : 'Could not add the series.'),
  });

  return (
    <Card className="p-3">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Category">
          <Select
            value={category}
            onChange={(e) => setCategory(e.target.value as TaxonomyCategory)}
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {CATEGORY_LABELS[c]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Series">
          <Input
            value={series}
            onChange={(e) => setSeries(e.target.value)}
            placeholder="e.g. TESTLINE Tour"
          />
        </Field>
        <div className="min-w-64 flex-1">
          <Field label="Aliases (comma-separated)">
            <Input
              value={aliases}
              onChange={(e) => setAliases(e.target.value)}
              placeholder="TESTLINE, TL Tour"
            />
          </Field>
        </div>
        <Button onClick={() => add.mutate()} disabled={add.isPending || series.trim() === ''}>
          {add.isPending ? 'Adding…' : 'Add series'}
        </Button>
      </div>
      {error && <p className="mt-2 text-xs text-danger-text">{error}</p>}
    </Card>
  );
}

function SeriesRowItem({
  row,
  canWrite,
  onSaved,
}: {
  row: TaxonomySeriesRow;
  canWrite: boolean;
  onSaved: () => void;
}) {
  const [aliases, setAliases] = useState(row.aliases.join(', '));

  const save = useMutation({
    mutationFn: () =>
      api.put(`/api/taxonomy/series/${row.id}`, {
        aliases: aliases
          .split(',')
          .map((a) => a.trim())
          .filter((a) => a !== ''),
      }),
    onSuccess: onSaved,
  });

  return (
    <tr>
      <td className="whitespace-nowrap px-3 py-1.5 text-body">
        {CATEGORY_LABELS[row.category] ?? row.category}
      </td>
      <td className="px-3 py-1.5 font-medium text-strong">{row.series}</td>
      <td className="px-3 py-1.5 text-muted">{row.aliases.join(', ') || '—'}</td>
      {canWrite && (
        <td className="px-3 py-1.5">
          <div className="flex items-center gap-2">
            <Input value={aliases} onChange={(e) => setAliases(e.target.value)} className="w-56" />
            <Button variant="secondary" onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </td>
      )}
    </tr>
  );
}
